/**
 * Mic-health monitor for the Live Examination UI.
 *
 * Problem: candidates using Bluetooth earpods / USB headsets sometimes
 * have their microphone track silently switch or disconnect mid-test.
 * MediaRecorder keeps writing "silent" audio chunks, the VAD shows no
 * voice, and the whole test becomes unusable — the candidate doesn't
 * realise anything is wrong until they see their transcript is empty.
 *
 * This module listens for:
 *   - `navigator.mediaDevices.devicechange` (earpod (dis)connects)
 *   - The recording track's `ended` state (mic hard-fail)
 *   - Sustained near-zero RMS audio (dead/muted mic even while track
 *     reports "live"). Threshold: RMS < 0.005 for 6+ consecutive seconds.
 *
 * On any of those signals, a callback fires so the caller (LiveTest.jsx)
 * can surface a persistent warning banner to the candidate with clear
 * recovery steps. We deliberately DO NOT try to auto-recreate the stream
 * — re-calling getUserMedia mid-test is disruptive and the native
 * recorder loses its header. The candidate re-selecting their mic from
 * the OS sound settings is the cleanest recovery.
 */

export function startMicHealthMonitor(stream, { onSilent, onDeviceChange, onTrackEnded } = {}) {
  if (!stream) return { stop() {} };
  const track = stream.getAudioTracks ? stream.getAudioTracks()[0] : null;
  let cancelled = false;
  let silenceSince = null;

  // Minimal AudioContext wiring. Kept separate from the VAD instance so
  // we can stop it independently and so VAD errors don't take out mic
  // health monitoring.
  let ctx = null;
  let analyser = null;
  let data = null;
  try {
    ctx = new (window.AudioContext || window.webkitAudioContext)();
    const src = ctx.createMediaStreamSource(stream);
    analyser = ctx.createAnalyser();
    analyser.fftSize = 512;
    data = new Uint8Array(analyser.fftSize);
    src.connect(analyser);
  } catch (_e) { /* AudioContext unavailable — silently skip RMS */ }

  const tick = () => {
    if (cancelled || !analyser) return;
    try {
      analyser.getByteTimeDomainData(data);
      let sumSq = 0;
      for (let i = 0; i < data.length; i++) {
        const v = (data[i] - 128) / 128;
        sumSq += v * v;
      }
      const rms = Math.sqrt(sumSq / data.length);
      if (rms < 0.005) {
        if (!silenceSince) silenceSince = Date.now();
        else if (Date.now() - silenceSince > 6000) {
          silenceSince = Date.now() + 60_000; // rearm — stop pinging for 60s
          onSilent && onSilent({ reason: "sustained-silence", rms });
        }
      } else {
        silenceSince = null;
      }
    } catch (_e) { /* keep monitor alive across transient errors */ }
  };
  const handle = setInterval(tick, 500);

  const onEnded = () => onTrackEnded && onTrackEnded({ track });
  if (track) track.addEventListener("ended", onEnded);

  const onDC = () => onDeviceChange && onDeviceChange();
  if (navigator.mediaDevices && navigator.mediaDevices.addEventListener) {
    navigator.mediaDevices.addEventListener("devicechange", onDC);
  }

  return {
    stop() {
      cancelled = true;
      clearInterval(handle);
      if (track) { try { track.removeEventListener("ended", onEnded); } catch (_e) {} }
      if (navigator.mediaDevices && navigator.mediaDevices.removeEventListener) {
        try { navigator.mediaDevices.removeEventListener("devicechange", onDC); } catch (_e) {}
      }
      try {
        if (ctx && ctx.state !== "closed") {
          const p = ctx.close();
          if (p && typeof p.catch === "function") p.catch(() => {});
        }
      } catch (_e) {}
    },
  };
}
